/**
 * dischargeModel — PURE state model for the C5/R3-4 discharge dialog (FE-2).
 * No React, no API calls: given a discharge type + the SERVER's cached
 * `balance_paise` (single source — never re-derived) + the fields entered so
 * far, it derives everything the dialog needs to render:
 *
 *   - which money action drives the balance to ₹0 (Collect vs Return advance)
 *   - whether the LAMA/DAMA/DECEASED residual is eligible for the permissioned
 *     write-off exception, and whether THIS actor may use it (R3-4/S4)
 *   - which fields are required for the current type (reason / cause of
 *     death / referral / authorised-by)
 *   - whether "Complete …" is enabled, its exact ratified label, and the
 *     plain-English reason it's disabled (every disabled state states WHY)
 *
 * R3-4 (S4, [REVERSAL-3]) replaced the old soft "record as dues" path
 * (DISCHARGE_WITH_DUES, `dues_ack_refused`) with a HARD zero-balance gate for
 * every discharge type; LAMA/DAMA/DECEASED alone may bypass it via a
 * permissioned write-off exception (WRITE_OFF_DUES — the SAME slug already
 * used by the Payment Pending queue's write-off, no new slug). A
 * non-permissioned actor never sees the exception — they see the same
 * "settle to discharge" hard block as NORMAL/REFERRED.
 *
 * bk-584 REVERSED (owner ruling 2026-08-29, bk-781) — DECEASED used to be
 * the ONE exception to the write-off rule above: a death with a positive
 * residual was recordable by ANY holder of DISCHARGE_PATIENT, no
 * `WRITE_OFF_DUES`, no authoriser, no reason, the debt staying owed
 * (`dues_status=OPEN`), never written off. The owner reversed this: in
 * practice there was no usable way to collect against a deceased discharge,
 * so that residual was effectively unrecoverable and the "stays owed,
 * collectable afterwards" justification did not hold. DECEASED is now
 * byte-identical to LAMA/DAMA — a positive residual needs the SAME
 * `WRITE_OFF_DUES` exception (authoriser + reason); a non-permissioned actor
 * sees the same hard block NORMAL/REFERRED already give. A death at 02:00
 * with unpaid dues is now BLOCKED until an admin authorises a write-off —
 * a deliberate, owner-accepted consequence, not a bug.
 *
 * Strings are pasted from the ratified copy in
 * `state/epics/IPD-MVP/round3-invoice-accounting/stories.md` §S4 AC2-4 (the
 * C5 copy-deck strings for the unchanged paths carry over verbatim).
 */

import {
  DISCHARGE_TYPE,
  DUES_ELIGIBLE_DISCHARGE_TYPES,
  DISCHARGE_ERROR_CODE,
  DOCTOR_REQUIRED_DISCHARGE_COPY,
  NO_BILL_CONFIRM_BODY,
  BILL_OPEN_NO_AMOUNT_COPY,
  NOT_TREATING_DOCTOR_DISCHARGE_COPY,
  PLAN_DISCHARGE_MAX_BACKDATE_DAYS,
  DISCHARGE_DATE_DESK_MAX_BACK_DAYS,
  DISCHARGE_DATE_FUTURE_GRACE_MS,
  ADMISSION_EVENT_KIND,
  ADMISSION_ACCESS_ERROR_CODE,
  ADMISSION_ACCESS_ERROR_COPY,
  MOVE_CHARGES_REASON,
  FIX_CHARGES_ACTION,
  isTerminal,
} from '../../../constants/admission';
import { STAFF_STATUS } from '../../../constants/staff';
import { describeBalance } from '../bill/billModel';
import { formatPaise, formatServiceDate, formatServiceDateShort } from '../../../utils/money';
// S4 (FE-4, C4 frozen contract) — the ONE IST datetime formatter (FE-2), reused
// verbatim so the Complete-step confirm line can never drift from the
// workspace/desk/running-bill chip's own rendering of the same instant.
import { formatDischargeDateTimeIST, splitISTValue, joinISTValue } from './planDischargeModel';
// DOD rule v3 (C5) — the ONE IST calendar-day helper the codebase already
// uses for length-of-stay. NEVER `toISOString().slice(0,10)`: that buckets
// 00:00–05:30 IST into the previous day and has produced this exact bug twice
// on this epic.
import { istCalendarDayIndex, utcIsoToISTInputValue, istInputValueToUtcIso } from '../../../utils/formatIST';

const isBlank = (s) => s == null || String(s).trim().length === 0;

function isDuesEligible(type) {
  return DUES_ELIGIBLE_DISCHARGE_TYPES.has(type);
}

/** §2.3's button relabel reads "death" (lowercase), not the LAMA/DAMA acronym form. */
function typeWord(type) {
  return type === DISCHARGE_TYPE.DECEASED ? 'death' : type;
}

/** R3-4/S4 AC2 — the ratified hard-block copy for an outstanding charges balance. */
function outstandingBalanceLine(balanceDesc) {
  return `Cannot discharge — outstanding balance of ${balanceDesc.text}. Collect all dues before discharge.`;
}

/** R3-4/S4 AC4 — the ratified hard-block copy for an excess (unrefunded) advance. */
function excessAdvanceLine(balanceDesc) {
  return `Refund excess advance of ${balanceDesc.text} to the patient before discharge.`;
}

/**
 * R3-4/S4 AC6 — the ratified button label for the permissioned exception.
 * O4 (Wave-2): drops the "LAMA" acronym — the exception covers LAMA/DAMA/
 * DECEASED alike, so the label is discharge-type-neutral.
 */
const WRITE_OFF_COMPLETE_LABEL = 'Discharge with write-off (exception)';

/**
 * admission-access (S4-AC6) — a caller whose money is withheld gets the amount
 * extras stripped (the server keeps the code): `NO_BILL_CONFIRM_BODY` (the
 * Complete confirm) and `BILL_OPEN_NO_AMOUNT_COPY` (every amount-bearing
 * refusal) are the ratified amount-free lines, in `constants/admission.js`.
 */
const AMOUNT_BEARING_DISCHARGE_CODES = new Set([
  DISCHARGE_ERROR_CODE.BALANCE_NOT_ZERO,
  DISCHARGE_ERROR_CODE.NOT_PERMITTED_DUES,
  DISCHARGE_ERROR_CODE.UNSETTLED_LAB_CREDIT,
  DISCHARGE_ERROR_CODE.REFUND_ADVANCE_FIRST,
  DISCHARGE_ERROR_CODE.DUES_NEGATIVE_BALANCE,
]);

/**
 * DOD rule (discharge-final) — the CLIENT MIRROR of the server's initiate
 * ladder `validate_confirmed_dod`. The discharge date is FINALISED at Initiate
 * (today or past only); Complete just stamps it. Ladder order is FIXED and the
 * FIRST failure wins, so the desk is shown EXACTLY ONE reason. Copy = PO
 * approval K1-K13 (po-approval-discharge-final.md).
 */
export const DOD_BEFORE_ADMISSION_ERROR = (admittedAt) =>
  `Cannot be before the admission date & time (${formatDischargeDateTimeIST(admittedAt)}).`;
export const DOD_IN_FUTURE_ERROR = 'The discharge date cannot be in the future. Use today or an earlier date.';
export const DOD_WALK_OUT_IN_FUTURE_ERROR = 'The time the patient left cannot be in the future.';
// K4 — role-neutral (doctors hold the permission too). `earliest` = "09 Oct 2026".
export const DOD_ADMIN_ONLY_ERROR_WITH_DATE = (earliest) =>
  `You can backdate up to ${DISCHARGE_DATE_DESK_MAX_BACK_DAYS} days (earliest ${earliest}). Ask an administrator to record an earlier date.`;
export const DOD_ADMIN_ONLY_ERROR = `You can backdate up to ${DISCHARGE_DATE_DESK_MAX_BACK_DAYS} days. Ask an administrator to record an earlier date.`;
export const DOD_TOO_OLD_ERROR = `Cannot be more than ${PLAN_DISCHARGE_MAX_BACKDATE_DAYS} days back.`;
export const DOD_FY_MISMATCH_ERROR = (fy) => `Must be in the same financial year as the invoice (${fy}).`;
export const DOD_REASON_REQUIRED_ERROR = 'Please enter a reason.';
export const DOD_REASON_MAX_LENGTH = 300;
// K3
export const DOD_REASON_LABEL = 'Reason for backdating *';
export const dodBackdateHint = (days) => `${days} ${days === 1 ? 'day' : 'days'} back. A reason is required.`;
// K1 / K6 / K6b
export const DOD_INITIATE_HELPER = 'This date is final. Once you initiate, the discharge date cannot be changed. If it is wrong, cancel the initiation and initiate again.';
export const DOD_WALK_OUT_HEADING = 'When did the patient leave?';
export const DOD_WALK_OUT_REASON_LABEL = 'Reason *';
export const DOD_WALK_OUT_FINAL_BANNER = 'This time is final. To fix it, cancel the initiation and initiate again.';
// K9 — summary note (the date is read-only once initiated).
export const DISCHARGE_DATE_CONFIRMED_NOTE = 'Final once discharge is initiated. To fix it, cancel the initiation and initiate again.';
// DISCHARGE_DATE_READ_ONLY (PATCH / summary PUT after Initiate).
export const DOD_READ_ONLY_ERROR = 'The discharge date is final once discharge is initiated.';
export const DOD_NOT_EDITABLE_ERROR = 'The discharge is complete. Correct the date through a bill correction.';
export const DOD_NO_PERMISSION_REASON = 'Only the billing desk or an administrator can record the discharge date.';
export const DOD_DECEASED_LOCK_REASON = 'Recorded as deceased. Complete the discharge to record the time of death.';
// DEPRECATED thin exports kept ONLY so DischargeDialog.jsx still compiles; FE-2 deletes them.
// K11 — death: the discharge date IS the recorded time of death.
export const dodDeathLine = (whenIso) =>
  `The discharge date on the bill and register will be the time of death: ${formatDischargeDateTimeIST(whenIso)}.`;
export const DOD_DEATH_FY_FALLBACK = "The time of death is in the previous financial year, so today's date will be used as the discharge date. The death summary still shows the actual time of death.";
// Business C3 — non-blocking note at Complete when today is later than the finalised date.
export const LATE_COMPLETE_NOTE = (whenText) =>
  `The discharge date is ${whenText}, which is earlier than today. If the patient left later, cancel the initiation from the More menu and initiate again with the correct time.`;
// K2
export const dodRecordedLine = (whenIso) => `Discharge will be recorded as ${formatDischargeDateTimeIST(whenIso)}.`;
export const dodBillPeriodLine = (whenIso) =>
  `The bill period will end on ${dateOnly(formatDischargeDateTimeIST(whenIso))}, or on the date of a later charge.`;

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** "13 Oct 2026, 6:00 PM IST" -> "13 Oct 2026". */
function dateOnly(formatted) {
  return formatted ? formatted.split(',')[0] : '';
}

/** "2026-10-09" (server `earliest`) -> "09 Oct 2026". Noon IST keeps it on the same IST day. */
function formatYmd(ymd) {
  return dateOnly(formatDischargeDateTimeIST(istInputValueToUtcIso(`${ymd}T12:00`)));
}

/**
 * dodDaysBack — whole IST calendar days between `now` and the value (0 = today
 * or a later day). The ONE day-count behind the 7-day / 30-day / reason rungs
 * (server: `local_date_of`, never a UTC slice).
 */
export function dodDaysBack(valueIso, now = new Date()) {
  const v = istCalendarDayIndex(valueIso);
  const n = istCalendarDayIndex(now.toISOString());
  return v == null || n == null ? 0 : n - v;
}

/**
 * istFinancialYear — Indian FY (Apr 1 – Mar 31) of a UTC ISO instant, IN IST
 * (matches the C3 contract's own `ist_financial_year(planned) !=
 * ist_current_financial_year()` formula, mirrored client-side — no backend
 * call). Returns null for an unparseable instant.
 */
function istFinancialYear(iso) {
  const utcMs = Date.parse(iso);
  if (Number.isNaN(utcMs)) return null;
  const ist = new Date(utcMs + IST_OFFSET_MS);
  const year = ist.getUTCFullYear();
  const month = ist.getUTCMonth() + 1; // 1-12
  return month >= 4 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
}

/**
 * fyStartYear — the opening year of an Indian FY label, whichever shape the
 * server sends it in ("2026-2027" or "2026-27"). The FY comparison is done on
 * this number, never on the raw label, so a formatting difference between the
 * stored `invoice_fy` and the client's own `istFinancialYear` can never
 * fabricate an FY error on screen.
 */
function fyStartYear(fy) {
  const n = parseInt(String(fy || '').slice(0, 4), 10);
  return Number.isNaN(n) ? null : n;
}

/**
 * validateConfirmedDod — the CLIENT MIRROR of the server's initiate ladder.
 * Returns the FIRST failure only (`{valid:false, code, reason}`) or
 * `{valid:true}`. Order (identical to the server): 1 unparseable (silent) ·
 * 2 before admission · 3 in the future (> now + 5 min) · 4 non-admin more than
 * 7 IST days back (ADMIN_ONLY) · 5 more than 30 days back (TOO_OLD) · 6 other
 * financial year than the invoice anchor · 7 an earlier IST day with no reason.
 *
 * @param {string|null} valueIso   the candidate discharge date (ISO-8601 UTC ms)
 * @param {boolean} isAdmin        the ACTOR's role (rung 4 — never a permission slug)
 * @param {string|null} fyAnchor   stored `invoice_fy` (re-initiate) else the current IST FY
 * @param {string} reason          the backdating reason typed so far (rung 7)
 * @param {boolean} walkOut        LAMA/DAMA "When did the patient leave?" — future copy only
 */
export function validateConfirmedDod({
  valueIso, admittedAt = null, isAdmin = false, now = new Date(), fyAnchor = null,
  reason = '', walkOut = false,
} = {}) {
  const valueMs = Date.parse(valueIso);
  if (!valueIso || Number.isNaN(valueMs)) return { valid: false, code: null, reason: null };
  const fail = (code, text) => ({ valid: false, code, reason: text });

  // 2 · on or after the admission moment.
  const admittedMs = Date.parse(admittedAt);
  if (admittedAt && !Number.isNaN(admittedMs) && valueMs < admittedMs) {
    return fail(DISCHARGE_ERROR_CODE.DISCHARGE_DATE_BEFORE_ADMISSION, DOD_BEFORE_ADMISSION_ERROR(admittedAt));
  }

  // 3 · never later than now (+ the server's clock-skew grace).
  if (valueMs > now.getTime() + DISCHARGE_DATE_FUTURE_GRACE_MS) {
    return fail(
      DISCHARGE_ERROR_CODE.DISCHARGE_DATE_IN_FUTURE,
      walkOut ? DOD_WALK_OUT_IN_FUTURE_ERROR : DOD_IN_FUTURE_ERROR,
    );
  }

  const daysBack = dodDaysBack(valueIso, now);

  // 4 · non-admin beyond the desk window => ADMIN only.
  if (daysBack > DISCHARGE_DATE_DESK_MAX_BACK_DAYS && !isAdmin) {
    const earliest = new Date(now.getTime() - DISCHARGE_DATE_DESK_MAX_BACK_DAYS * MS_PER_DAY).toISOString();
    return fail(
      DISCHARGE_ERROR_CODE.DISCHARGE_DATE_ADMIN_ONLY,
      DOD_ADMIN_ONLY_ERROR_WITH_DATE(dateOnly(formatDischargeDateTimeIST(earliest))),
    );
  }

  // 5 · role-blind 30-day ceiling (calendar days).
  if (daysBack > PLAN_DISCHARGE_MAX_BACKDATE_DAYS) {
    return fail(DISCHARGE_ERROR_CODE.DISCHARGE_DATE_TOO_OLD, DOD_TOO_OLD_ERROR);
  }

  // 6 · same financial year as the invoice serial.
  const nowIso = now.toISOString();
  const anchorLabel = fyAnchor || istFinancialYear(nowIso);
  const anchorYear = fyStartYear(anchorLabel);
  const valueYear = fyStartYear(istFinancialYear(valueIso));
  if (anchorYear != null && valueYear != null && anchorYear !== valueYear) {
    return fail(DISCHARGE_ERROR_CODE.DISCHARGE_DATE_FY_MISMATCH, DOD_FY_MISMATCH_ERROR(anchorLabel));
  }

  // 7 · an earlier IST day (any role) needs a reason; same-day earlier time does not.
  if (daysBack >= 1 && isBlank(reason)) {
    return fail(DISCHARGE_ERROR_CODE.DISCHARGE_DATE_REASON_REQUIRED, DOD_REASON_REQUIRED_ERROR);
  }

  return { valid: true, code: null, reason: null };
}

/**
 * defaultConfirmDodFields — Initiate prefill: the admission's PLAN only when it
 * is not in the future (a future plan can never be the finalised date), else
 * `now` in IST. `fromPlan` drives which helper the dialog renders.
 */
export function defaultConfirmDodFields(admission, now = new Date()) {
  const plan = (admission && admission.planned_discharge_at) || null;
  const planMs = Date.parse(plan);
  const usePlan = !Number.isNaN(planMs) && planMs <= now.getTime();
  const iso = usePlan ? plan : now.toISOString();
  return { ...splitISTValue(utcIsoToISTInputValue(iso)), fromPlan: usePlan };
}

/** The `{date, time}` IST wall-clock pair the date fields hold -> the wire ISO. */
export function confirmDodFieldsToIso(date, time) {
  return istInputValueToUtcIso(joinISTValue(date, time));
}

/**
 * buildInitiateBody — `POST /discharge/initiate` body. ALWAYS carries
 * `discharge_at`; `discharge_date_reason` (+ `discharge_date_reason_code`) only when the date is an earlier IST
 * day AND a reason was typed (switching back to today drops a stale reason).
 */
export function buildInitiateBody({ valueIso, reason = '', reasonCode = '', now = new Date() } = {}) {
  const body = { discharge_at: valueIso };
  const r = String(reason || '').trim();
  if (r && dodDaysBack(valueIso, now) >= 1) {
    body.discharge_date_reason = r;
    if (reasonCode) body.discharge_date_reason_code = reasonCode;
  }
  return body;
}

/** K2 — the Initiate confirm lines: the recorded date, + the bill-period line when backdated. */
export function initiateConfirmLines({ valueIso, now = new Date() } = {}) {
  const lines = [dodRecordedLine(valueIso)];
  if (dodDaysBack(valueIso, now) >= 1) lines.push(dodBillPeriodLine(valueIso));
  return lines;
}

/**
 * The three confirmed-date success sentences (Copy table). Built HERE so the
 * dialog that raises one and the workspace toast that echoes it (FE-2) can
 * never drift into two wordings of the same event.
 */
export const dodInitiatedToast = (iso) => `Discharge initiated. Discharge date recorded as ${formatDischargeDateTimeIST(iso)}.`;
export const dodCompletedToast = (iso) => `Discharge completed. Discharge date recorded as ${formatDischargeDateTimeIST(iso)}.`;

/** S2 AC3 — how many history rows are listed before the "+n more" overflow. */
export const DOD_HISTORY_VISIBLE_ROWS = 3;

/**
 * buildDodHistory — S2 AC3 / Screen B: the confirmed-date change history,
 * NEWEST FIRST, first `DOD_HISTORY_VISIBLE_ROWS` rows + a "+n more" overflow,
 * and ABSENT entirely when the date was never edited. ONE selector shared by
 * the Change dialog and the Details row (FE-2) so the two lists can never
 * show a different set.
 *
 * Each entry is `{from, to, reason, actor}` (from the existing
 * `ADMISSION_DETAILS_UPDATED` audit event's additive `refs.dod_edit`), already
 * in newest-first order.
 */
/**
 * dodEditsFromEvents — the `{from, to, reason, actor}` rows `buildDodHistory`
 * renders, read off the admission TIMELINE (`GET /admissions/{id}/events`,
 * OLDEST-first) — C3 mints no new event kind, a confirmed-date edit rides the
 * existing `ADMISSION_DETAILS_UPDATED` with additive `refs.dod_edit`. Reversed
 * to NEWEST-first here, once, so every consumer (the Details row list and the
 * Change dialog) reads the SAME ordered set.
 *
 * An event without a `dod_edit` ref (any other details edit) is skipped — an
 * admission whose date was never changed yields `[]`, and the history is then
 * absent entirely (S2 AC3), never an empty box.
 */
export function dodEditsFromEvents(events) {
  // The dod_edit ref rides ADMISSION_STATUS_CHANGED (Initiate with a reason)
  // and, for rows written before the release, ADMISSION_DETAILS_UPDATED.
  const rows = (Array.isArray(events) ? events : []).filter(
    (e) => e && (e.event_kind === ADMISSION_EVENT_KIND.STATUS_CHANGED
      || e.event_kind === ADMISSION_EVENT_KIND.DETAILS_UPDATED)
      && e.refs && e.refs.dod_edit,
  );
  return rows.reverse().map((e) => ({
    from: e.refs.dod_edit.from,
    to: e.refs.dod_edit.to,
    reason: e.refs.dod_edit.reason,
    actor: e.actor && e.actor.by_system ? 'System' : ((e.actor && e.actor.name) || ''),
    at: e.occurred_at || null,
  }));
}

/**
 * currentDodEdit — the dod_edit of the CURRENT initiation only: the newest
 * edit recorded AFTER the latest cancel-initiation event (null when the
 * re-initiation was not backdated). Withdrawn initiations never show.
 */
export function currentDodEdit(events) {
  const list = Array.isArray(events) ? events : [];
  const cancels = list.filter((e) => e && e.refs && e.refs.cancel_initiation && e.occurred_at);
  const cancelAt = cancels.length ? Math.max(...cancels.map((e) => new Date(e.occurred_at).getTime())) : null;
  const after = cancelAt == null ? list : list.filter((e) => e && e.occurred_at && new Date(e.occurred_at).getTime() > cancelAt);
  return dodEditsFromEvents(after)[0] || null;
}

/**
 * dodBackdatedLine — K7 for the Details row: "Backdated 3 days · Reason: … (by
 * Anita, 16 Oct, 9:02 AM)". `edit` is a `dodEditsFromEvents` row; days = IST
 * days between when it was recorded (`at`) and the date it set (`to`). null
 * when not backdated.
 */
export function dodBackdatedLine(edit) {
  if (!edit) return null;
  const days = dodDaysBack(edit.to, edit.at ? new Date(edit.at) : new Date());
  if (days < 1) return null;
  const when = edit.at
    ? formatDischargeDateTimeIST(edit.at).replace(/ \d{4},/, ',').replace(/ IST$/, '')
    : '';
  const by = [edit.actor, when].filter(Boolean).join(', ');
  return `Backdated ${days} ${days === 1 ? 'day' : 'days'} \u00b7 Reason: ${edit.reason || ''}${by ? ` (by ${by})` : ''}`;
}

export function buildDodHistory(history = [], { showAll = false } = {}) {
  const all = (Array.isArray(history) ? history : []).filter(Boolean).map((h) => ({
    key: `${h.from || ''}|${h.to || ''}|${h.reason || ''}|${h.actor || ''}`,
    text: `${formatDischargeDateTimeIST(h.from)} → ${formatDischargeDateTimeIST(h.to)} · ${h.reason || ''} · ${h.actor || ''}`,
  }));
  const overflow = Math.max(0, all.length - DOD_HISTORY_VISIBLE_ROWS);
  // FIX-16 (business) — "+n more" was INERT. It was always specified to
  // EXPAND IN PLACE (first 3, then the rest), so the selector owns the
  // switch and both consumers read the same shape.
  return {
    rows: showAll ? all : all.slice(0, DOD_HISTORY_VISIBLE_ROWS),
    overflowLabel: overflow > 0 && !showAll ? `+${overflow} more` : null,
    overflowCount: overflow,
    expanded: !!showAll && overflow > 0,
  };
}

/**
 * deathDodFyFallback — a death in another financial year than the invoice
 * anchor cannot be the discharge date (server: now + audit); K11 tells the user.
 */
export function deathDodFyFallback({ deathAt, fyAnchor = null, now = new Date() } = {}) {
  const deathYear = fyStartYear(istFinancialYear(deathAt));
  const anchorYear = fyStartYear(fyAnchor || istFinancialYear(now.toISOString()));
  return deathYear != null && anchorYear != null && deathYear !== anchorYear;
}

/**
 * resolveDischargeDateTimeIso — the ONE instant "the discharge date & time"
 * resolves to at Complete (every surface calls this SAME function). Complete
 * no longer chooses: it stamps what Initiate finalised.
 *   - NORMAL/REFERRED/LAMA/DAMA: the finalised date, clamped to <= now; absent => now.
 *   - DECEASED: the recorded time of death (no day limit); absent, or in another
 *     financial year than the invoice => now.
 */
export function resolveDischargeDateTimeIso({
  dischargeType, confirmedDischargeAt = null, deathAt = null, now = new Date(), fyAnchor = null,
} = {}) {
  const nowIso = now.toISOString();
  if (dischargeType === DISCHARGE_TYPE.DECEASED) {
    if (Number.isNaN(Date.parse(deathAt))) return nowIso;
    return deathDodFyFallback({ deathAt, fyAnchor, now }) ? nowIso : deathAt;
  }
  const ms = Date.parse(confirmedDischargeAt);
  if (Number.isNaN(ms) || ms > now.getTime()) return nowIso;
  return confirmedDischargeAt;
}

/**
 * buildAuthoriserOptions — O3 staff-member picker source. Maps the org staff
 * roster (GET /mem via `fetchStaffList`) to the `{ member_id, name, role }`
 * options the write-off "Authorised by" picker renders. Active staff only
 * (a disabled/inactive member cannot authorise). Pure + testable; the exact
 * member id it yields is what the dialog submits as `authorised_by_member_id`.
 */
export function buildAuthoriserOptions(staff = []) {
  return (staff || [])
    .filter((m) => m && (m.member_id || m.staff_id || m.id) && m.status === STAFF_STATUS.ACTIVE)
    .map((m) => {
      const memberId = m.member_id || m.staff_id || m.id;
      const name = `${m.first_name || ''} ${m.last_name || ''}`.trim();
      return { member_id: memberId, name: name || m.username || memberId, role: m.role || null };
    });
}

/**
 * @param {object} args
 * @param {string|null} args.dischargeType   DISCHARGE_TYPE key, or null if unselected
 * @param {number|null} args.balancePaise    server-cached signed balance (single source). `null` =
 *   WITHHELD (admission-access: `money_visible === false`) — the gate predicts nothing from it:
 *   no collect/refund/write-off, no balance block (`gate.balanceKnown === false`), and Complete is
 *   enabled so the SERVER stays the one judge of the balance. `undefined` keeps the 0 default.
 * @param {string} [args.reason]             LAMA/DAMA reason, or DECEASED/write-off narrative
 * @param {{immediate_cause?:string, antecedent_cause?:string, death_at?:string}|null} [args.causeOfDeath]
 * @param {string} [args.referralReason]     REFERRED "Reason for referral" text (→ contract `referral_reason`)
 * @param {string} [args.referredTo]         REFERRED "Referred to" facility name (→ contract `referral_note` —
 *   the frozen body has no dedicated facility field; FE stores it in the optional
 *   `referral_note` and requires it CLIENT-SIDE, since the discharge-summary print
 *   (copy-deck §5) needs a distinct "Referred to: {facility}" line — see FE-2
 *   integration-notes.md for this assumption)
 * @param {string} [args.authorisedByMemberId]  member id of the staff member authorising a
 *   write-off exception (O3 — a staff PICKER, no longer free text; the server resolves the
 *   display name from this id). Non-empty ⇒ the "Authorised by" requirement is satisfied.
 * @param {boolean} [args.canWriteOffDues]   holds WRITE_OFF_DUES (ADMIN-only; R3-4 reuses this
 *   existing slug for the discharge-time exception — no new slug)
 * @param {boolean} [args.labWriteOffPending] BUGFIX/lama-discharge round 2 — sticky client flag:
 *   a prior server refusal has told the dialog that THIS dues-eligible, zero-cash-balance
 *   discharge is absorbing unsettled LAB credit as a write-off (the exact paise figure is not
 *   always available — `AuthorisedByRequired` carries no extras — so this is a boolean "we now
 *   know", not a re-derivation of the amount). Caller sets it once and never clears it (R1 —
 *   sticky reveal survives a re-render / stale submit).
 * @param {string|null} [args.confirmedDischargeAt] DOD rule v3 — the admission's CONFIRMED
 *   discharge date (stored on `planned_discharge_at`, its meaning carried by the
 *   `discharge_date_confirmed_at` marker — single source, never re-derived here);
 *   null/absent ⇒ nothing confirmed. Feeds `gate.stampChoice` (C5) — never used to block
 *   `completeEnabled` (Now is always available, so a guard failure disables only the
 *   "Confirmed" radio, not Complete).
 * @param {string|null} [args.admittedAt] the admission moment — C2 ladder rule 1.
 * @param {string|null} [args.fyAnchor]   the admission's stored `invoice_fy` — C2 ladder rule 5 (R2).
 * @param {boolean} [args.isAdmin]           S4 (FE-4, AC-I9) — the ACTOR's role is ADMIN
 *   (mirrors the server's role check, `ipd_charge_service.py:586` pattern — role-based,
 *   never a permission slug).
 * @param {Date} [args.now]                  client clock instant, injectable for tests.
 */
export function buildDischargeGate({
  dischargeType = null,
  balancePaise = 0,
  reason = '',
  causeOfDeath = null,
  referralReason = '',
  referredTo = '',
  authorisedByMemberId = '',
  canWriteOffDues = false,
  labWriteOffPending = false,
  confirmedDischargeAt = null,
  admittedAt = null,
  fyAnchor = null,
  isAdmin = false,
  now = new Date(),
} = {}) {
  const balanceKnown = balancePaise !== null;
  const balance = balanceKnown ? Math.round(Number(balancePaise) || 0) : 0;
  const balanceDesc = describeBalance(balance);
  const duesEligible = isDuesEligible(dischargeType);
  const advanceHeld = balance < 0;

  // R3-4 [REVERSAL-3] — every discharge type now needs balance <= 0 to
  // complete normally. LAMA/DAMA/DECEASED alone may bypass a POSITIVE
  // residual via the permissioned write-off exception (never the excess
  // advance case — that is the patient's money, never written off).
  //
  // bk-584 REVERSED (owner ruling 2026-08-29, bk-781) — DECEASED used to be
  // excluded here (a separate `deathOpenDues` flag routed it to a soft
  // OPEN-dues "record death" action, no authoriser, no reason). It no
  // longer is: DECEASED is now byte-identical to LAMA/DAMA on this branch.
  const writeOffEligible = duesEligible && balance > 0;
  const writeOffAuthorised = writeOffEligible && canWriteOffDues;

  // BUGFIX/lama-discharge round 2 (R5) — the SIBLING write-off state: a
  // dues-eligible discharge at a SETTLED-OR-BETTER cash balance can still
  // need the same permissioned exception when unsettled LAB credit is being
  // absorbed as bad-debt (`absorb_lab` server-side). Gated on STATE
  // (`labWriteOffPending`, set by the dialog from a refusal), never on
  // discharge type — DECEASED hits the identical branch as LAMA/DAMA (this
  // was already true before the bk-584 reversal; only the pure-charges
  // residual above was ever carved out).
  // Mutually exclusive with `writeOffEligible` (balance > 0 vs balance <= 0).
  const labWriteOffEligible = duesEligible && balanceKnown && balance <= 0 && !!labWriteOffPending;
  const labWriteOffAuthorised = labWriteOffEligible && canWriteOffDues;

  // Money actions — never both offered at once; a write-off-eligible type
  // still offers Collect as a nudge so the residual can shrink before completing.
  const offerCollect = balance > 0;
  const offerRefund = advanceHeld;

  // ── Field requirements (frozen contract #3 body rules) ────────────────
  // LAMA/DAMA always require a reason (it doubles as "reason for this
  // discharge", independent of balance). bk-584 REVERSED — DECEASED now
  // requires one too, but ONLY when a write-off is actually being taken
  // (mirrors the server's conditional check): a zero-balance death still
  // needs no reason (cause of death already covers "why").
  const reasonRequired = dischargeType === DISCHARGE_TYPE.LAMA
    || dischargeType === DISCHARGE_TYPE.DAMA
    || (dischargeType === DISCHARGE_TYPE.DECEASED && (writeOffEligible || labWriteOffEligible));
  const causeOfDeathRequired = dischargeType === DISCHARGE_TYPE.DECEASED;
  const referralRequired = dischargeType === DISCHARGE_TYPE.REFERRED;
  // Authorised-by is collected on EITHER write-off exception path — the cash
  // residual (writeOffAuthorised) or the absorbed-lab-credit sibling
  // (labWriteOffAuthorised, round 2) — never for a zero-residual/no-lab
  // dues-eligible discharge or any NORMAL/REFERRED flow. ONE flag drives
  // both the picker's render AND the submitted payload (DischargeDialog.jsx)
  // — do not fork this into a second flag.
  const authorisedByRequired = writeOffAuthorised || labWriteOffAuthorised;

  const reasonMissing = reasonRequired && isBlank(reason);
  const causeOfDeathMissing = causeOfDeathRequired
    && (!causeOfDeath || isBlank(causeOfDeath.immediate_cause) || isBlank(causeOfDeath.death_at));
  const referralMissing = referralRequired && (isBlank(referralReason) || isBlank(referredTo));
  const authorisedByMissing = authorisedByRequired && isBlank(authorisedByMemberId);
  const fieldsMissing = reasonMissing || causeOfDeathMissing || referralMissing || authorisedByMissing;

  let completeLabel = 'Complete discharge';
  let completeEnabled = false;
  let disabledReason = null;

  if (!dischargeType) {
    disabledReason = 'Select a discharge type to continue.';
  } else if (advanceHeld) {
    // AC4 — excess advance blocks EVERY discharge type; never bypassable
    // (returning the patient's own money is not a write-off).
    disabledReason = excessAdvanceLine(balanceDesc);
  } else if (duesEligible) {
    const word = typeWord(dischargeType);
    if (writeOffEligible) {
      if (!writeOffAuthorised) {
        // AC-NEG — a non-permissioned actor never reaches the exception; they
        // see the same hard-block "settle to discharge" copy as NORMAL/REFERRED.
        disabledReason = outstandingBalanceLine(balanceDesc);
      } else {
        completeLabel = WRITE_OFF_COMPLETE_LABEL;
        if (fieldsMissing) {
          disabledReason = 'Fill the required fields above to complete this discharge.';
        } else {
          completeEnabled = true;
        }
      }
    } else if (labWriteOffAuthorised) {
      // R2 (round 2) — reuse the SAME write-off label; a write-off genuinely
      // is being taken (unsettled lab absorbed as bad-debt), same action as
      // the cash-residual case. A non-permissioned actor never reaches this
      // branch (labWriteOffAuthorised requires canWriteOffDues), so they fall
      // through to the plain `else` below — UNCHANGED from today.
      completeLabel = WRITE_OFF_COMPLETE_LABEL;
      if (fieldsMissing) {
        disabledReason = 'Fill the required fields above to complete this discharge.';
      } else {
        completeEnabled = true;
      }
    } else {
      completeLabel = `Complete ${word} discharge`;
      if (fieldsMissing) {
        disabledReason = 'Fill the required fields above to complete this discharge.';
      } else {
        completeEnabled = true;
      }
    }
  } else if (dischargeType === DISCHARGE_TYPE.REFERRED) {
    if (balance > 0) {
      disabledReason = outstandingBalanceLine(balanceDesc);
    } else {
      completeLabel = 'Complete discharge & referral';
      if (fieldsMissing) {
        disabledReason = 'Fill the required fields above to complete this discharge.';
      } else {
        completeEnabled = true;
      }
    }
  } else {
    // NORMAL
    if (balance > 0) {
      disabledReason = outstandingBalanceLine(balanceDesc);
    } else {
      completeEnabled = true;
    }
  }

  return {
    balanceKnown,
    balance: balanceKnown ? balanceDesc : null,
    duesEligible,
    writeOffEligible,
    writeOffAuthorised,
    labWriteOffAuthorised,
    advanceHeld,
    offerCollect,
    offerRefund,
    reasonRequired,
    causeOfDeathRequired,
    referralRequired,
    authorisedByRequired,
    fieldsMissing,
    completeLabel,
    completeEnabled,
    disabledReason,
  };
}

/**
 * unsettledLabLine — R3-4/S4 AC3 ratified copy, listing the unsettled-lab
 * block reason. `entries` (optional) is the frozen EP-1 `credit_entries`
 * array (`{credit_entry_id, test_name, outstanding_paise}`) — the caller
 * renders it as the "WHAT is unpaid" breakdown; this only builds the headline.
 */
function unsettledLabLine(unsettledLabPaise, entries) {
  const n = Array.isArray(entries) ? entries.length : 0;
  const total = describeBalance(Math.abs(Number(unsettledLabPaise) || 0)).text;
  return `Cannot discharge — ${n} unsettled lab charge${n === 1 ? '' : 's'} totalling ${total}. Settle in lab collections first.`;
}

/**
 * Maps the frozen machine error codes (contract #3 / R3-4 EP-1) to the
 * ratified inline copy. Returns `null` for a code with no dedicated dialog
 * string (caller falls back to the server's `message` / a generic error line).
 */
/** Owner 2026-10-13 — CHARGES_AFTER_DISCHARGE_DATE heading / body / hint (owner's exact copy). */
export const chargesAfterDischargeDateLine = (ymd) =>
  `Some charges are dated after the discharge date (${formatServiceDateShort(ymd) || ymd || 'the chosen date'}).`;
export const lateChargesBodyLine = (ymd) =>
  `Room and per-day charges will be removed — the patient was not admitted on those days. Other charges will be moved to ${formatServiceDateShort(ymd) || ymd}. Amounts don't change for moved charges.`;
export const dischargeDateAfterChargeFixLine = (ymd) =>
  `Charges were fixed for a discharge date of ${formatServiceDateShort(ymd) || ymd}. The discharge date can't be later than that. Ask your admin to change it.`;
export const LATE_CHARGES_HINT = 'If the patient actually stayed on these days, change the discharge date instead.';

/** One line per offending charge: "date · description · amount" (amount
 *  omitted when the server sent none — a money-hidden caller). What a line may
 *  do is the SERVER's decision (`allowed_actions` / `default_action` /
 *  `blocked_code`) — never re-derived here. */
export function chargesAfterDischargeDateItems(charges) {
  if (!Array.isArray(charges)) return [];
  return charges.map((c) => {
    const allowed = Array.isArray(c.allowed_actions) ? c.allowed_actions : [];
    return {
      key: `${c.source || ''}:${c.id}`,
      id: c.id,
      source: c.source,
      serviceDate: c.service_date,
      allowedActions: allowed,
      defaultAction: allowed.includes(c.default_action) ? c.default_action : null,
      blockedCode: c.blocked_code || null,
      blocked: allowed.length === 0,
      text: [
        formatServiceDate(c.service_date) || c.service_date,
        c.description,
        c.amount_paise != null ? formatPaise(c.amount_paise) : null,
      ].filter(Boolean).join(' · '),
    };
  });
}

/** The action each line starts on (the server's default). */
export const defaultLineActions = (items) =>
  Object.fromEntries((items || []).filter((c) => c.defaultAction).map((c) => [c.id, c.defaultAction]));

/** Done-lab line copy: which date clears it. */
export const labDoneLine = (ymd) =>
  `This test was done on ${formatServiceDateShort(ymd) || ymd}. Change the discharge date to ${formatServiceDateShort(ymd) || ymd} or later.`;

/** "Remove N · Move M" over the lines the desk is acting on. */
export function fixChargesCounts(items, actions) {
  const picked = (items || []).filter((c) => !c.blocked).map((c) => actions[c.id]);
  return {
    remove: picked.filter((a) => a === FIX_CHARGES_ACTION.REMOVE).length,
    move: picked.filter((a) => a === FIX_CHARGES_ACTION.MOVE).length,
  };
}
export const fixChargesButtonCount = ({ remove, move }) => `Remove ${remove} · Move ${move}`;

/** Fix request body. OTHER carries the typed text; the two fixed reasons are
 *  worded by the server. `null` while the pick is incomplete or a done lab blocks. */
export function buildFixChargesBody({ dischargeDate, items, actions, reasonCode, otherText }) {
  const list = items || [];
  if (!dischargeDate || !reasonCode || !list.length || list.some((c) => c.blocked)) return null;
  const picked = list.map((c) => ({ id: c.id, action: actions[c.id] }));
  if (picked.some((i) => !i.action)) return null;
  if (reasonCode === MOVE_CHARGES_REASON.OTHER) {
    const reason = (otherText || '').trim();
    return reason ? { discharge_date: dischargeDate, items: picked, reason_code: reasonCode, reason } : null;
  }
  return { discharge_date: dischargeDate, items: picked, reason_code: reasonCode };
}

export function dischargeErrorMessage(code, extras = {}, { moneyHidden = false } = {}) {
  // admission-access (S4-AC6) — a caller whose money is withheld gets the amount
  // extras stripped; the amount-bearing refusals would otherwise read their
  // missing figures as ₹0 ("0 unsettled lab charges totalling ₹0") or as a
  // settled bill. One amount-free line for all of them.
  if (moneyHidden && AMOUNT_BEARING_DISCHARGE_CODES.has(code)) return BILL_OPEN_NO_AMOUNT_COPY;
  switch (code) {
    case DISCHARGE_ERROR_CODE.BALANCE_CHANGED: {
      if (extras.balance_paise == null) return 'The bill has changed. Please review before continuing.';
      const d = describeBalance(extras.balance_paise);
      return `The bill has changed. New balance: ${d.text}. Please review before continuing.`;
    }
    case DISCHARGE_ERROR_CODE.BALANCE_NOT_ZERO: {
      // R3-4 — BALANCE_NOT_ZERO now fires charges-only (NORMAL/REFERRED);
      // the sign check is defensive (never expected negative here).
      if (extras.balance_paise == null) return 'The balance must be ₹0 before this discharge can complete.';
      const d = describeBalance(extras.balance_paise);
      return d.kind === 'advance' ? excessAdvanceLine(d) : outstandingBalanceLine(d);
    }
    // R3-4/S4 AC3 (NEW) — unsettled lab-credit block.
    case DISCHARGE_ERROR_CODE.UNSETTLED_LAB_CREDIT:
      return unsettledLabLine(extras.unsettled_lab_paise, extras.credit_entries);
    // R3-4/S4 AC4 (NEW) — excess-advance block on the 409 path (mirrors the
    // client-side pre-check so a stale-balance race still shows the same copy).
    case DISCHARGE_ERROR_CODE.REFUND_ADVANCE_FIRST: {
      if (extras.refundable_advance_paise == null) return 'Refund the excess advance to the patient before discharge.';
      return excessAdvanceLine(describeBalance(-Math.abs(Number(extras.refundable_advance_paise) || 0)));
    }
    case DISCHARGE_ERROR_CODE.DUES_NEGATIVE_BALANCE: {
      const d = extras.balance_paise != null ? describeBalance(extras.balance_paise) : null;
      return d ? excessAdvanceLine(d) : 'Refund the excess advance to the patient before discharge.';
    }
    case DISCHARGE_ERROR_CODE.REASON_REQUIRED:
      return 'A reason is required to complete this discharge.';
    case DISCHARGE_ERROR_CODE.CAUSE_OF_DEATH_REQUIRED:
      return 'Fill the required fields above to complete this discharge.';
    // NOT_PERMITTED_DUES (existing code) is now the write-off-exception 403 —
    // a non-permissioned actor reaching the server never sees a dedicated
    // exception message, just the standard settle-to-discharge copy.
    //
    // T2 (BUGFIX/lama-discharge) — the server now ALSO sends
    // `unsettled_lab_paise` on this code (CONTRACT.md). A LAMA/DAMA discharge
    // with a settled cash bill but unpaid lab charges hit this same 403 (the
    // write-off exception covers absorbing that lab credit too), and the old
    // fallback ("Only an admin can authorise…") was incoherent on a screen
    // showing a ZERO balance with no write-off on offer. Branch order is
    // frozen by COPY.md — do not reorder:
    //   1. balance_paise > 0                        → existing outstanding-balance line.
    //   2. balance_paise <= 0, lab finite > 0        → NEW: name the lab credit, with amount.
    //   3. balance_paise <= 0, lab not finite         → NEW: same line, no amount (FE-ahead-of-BE).
    //   4. balance_paise <= 0, lab === 0              → UNREACHABLE in practice (server only sets
    //      write_off_paise/absorb_lab, and thus only raises this code, when cached_balance > 0 or
    //      unsettled_lab > 0 — see discharge_service.py). Its fallthrough behaviour also SHIFTED
    //      versus the pre-contract build: extras used to arrive empty here, so this returned the
    //      phantom "Only an admin can authorise a write-off exception."; now balance_paise is
    //      always sent, so at balance 0 it returns outstandingBalanceLine(describeBalance(0))
    //      instead. Left as dead code, not repaired — tracked as bk-546.
    case DISCHARGE_ERROR_CODE.NOT_PERMITTED_DUES: {
      if (extras.balance_paise > 0) {
        return outstandingBalanceLine(describeBalance(extras.balance_paise));
      }
      const lab = Number(extras.unsettled_lab_paise);
      if (Number.isFinite(lab) && lab > 0) {
        const total = describeBalance(Math.abs(Number(extras.unsettled_lab_paise) || 0)).text;
        return `The cash bill is settled, but unpaid lab charges of ${total} are still open on this patient. Settle them in lab collections, or ask an admin to complete this discharge.`;
      }
      if (!Number.isFinite(lab)) {
        return 'The cash bill is settled, but unpaid lab charges are still open on this patient. Settle them in lab collections, or ask an admin to complete this discharge.';
      }
      return extras.balance_paise != null
        ? outstandingBalanceLine(describeBalance(extras.balance_paise))
        : 'Only an admin can authorise a write-off exception.';
    }
    // B2 (BUGFIX/lama-discharge round 2) — the write-off exception's authoriser
    // gate: `AuthorisedByRequired` fires whenever a permissioned actor submits
    // a write-off (cash residual OR absorbed lab credit) without an
    // `authorised_by_member_id`. It carries no extras (no amount to
    // interpolate), so the copy is static (COPY-R2.md (a)) — never a raw
    // server string on a zero-balance screen.
    case DISCHARGE_ERROR_CODE.AUTHORISED_BY_REQUIRED:
      return 'Choose the staff member authorising this discharge, then try again.';
    // admission-access (C-1) — a money-only route refused this caller: the
    // frozen PO copy (single source `ADMISSION_ACCESS_ERROR_COPY`), never the
    // wire `message`.
    case ADMISSION_ACCESS_ERROR_CODE.NOT_PRIMARY_DOCTOR:
    case ADMISSION_ACCESS_ERROR_CODE.MONEY_NOT_PERMITTED:
      return ADMISSION_ACCESS_ERROR_COPY[code];
    // A doctor who is not on this admission's care team ran a discharge step —
    // its OWN sentence (the shared map's copy names the summary, wrong here).
    case ADMISSION_ACCESS_ERROR_CODE.NOT_TREATING_DOCTOR:
      return NOT_TREATING_DOCTOR_DISCHARGE_COPY;
    case DISCHARGE_ERROR_CODE.USE_DISCHARGE_INTENT:
      return 'Use the discharge action to complete this admission.';
    // RATIFIED (copy pack, bk-747) — a stale local `currentStatus` re-firing
    // `clear` after it already committed used to fall through to the raw
    // server string ("Cannot transition from BILLING_CLEARANCE to
    // BILLING_CLEARANCE"). The dead-end itself is fixed by tracking the
    // SERVER's status after clear (see runComplete) — this mapping is the
    // safety net for any other stale-transition race.
    case DISCHARGE_ERROR_CODE.INVALID_TRANSITION:
      return 'This discharge has already moved past the billing step. Close this window and reopen it to finish the discharge.';
    // death-record slice (invariant 4/13, FE-6) — a death record exists and
    // this attempt was inconsistent with it. The UI already locks the type
    // picker and skips "Initiate discharge" whenever a record is open (see
    // DischargeDialog), so this is a defensive net for a race (e.g. a record
    // created by another actor between this dialog opening and Complete),
    // not a path the happy flow reaches. [NEEDS-PO]
    case DISCHARGE_ERROR_CODE.CHARGES_AFTER_DISCHARGE_DATE:
      return chargesAfterDischargeDateLine(extras.discharge_date);
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_AFTER_CHARGE_FIX:
      return dischargeDateAfterChargeFixLine(extras.fix_date);
    case DISCHARGE_ERROR_CODE.DEATH_RECORDED_TYPE_LOCKED:
      return 'A death is recorded on this admission — the discharge type is locked to Deceased. Close and reopen to see the locked type.';
    // death-record slice (invariant 6) — complete's write to the death record
    // lost a race (the record was edited/removed elsewhere since this dialog
    // opened). [NEEDS-PO]
    case DISCHARGE_ERROR_CODE.DEATH_RECORD_STALE:
      return 'The death record has changed since this discharge was opened. Close and reopen to see the latest.';
    // Owner ruling H (2026-09-13) — a defensive net for the race where the
    // admission's doctor was removed between this dialog opening and
    // Initiate/Complete; the client-side gate (`admission.primary_doctor_member_id`)
    // already disables both buttons in the normal case. Same ratified
    // sentence, single source — the DISCHARGE-surface one (integration
    // reconcile r4): here the user is discharging, not writing a sheet.
    case DISCHARGE_ERROR_CODE.DOCTOR_REQUIRED:
      return DOCTOR_REQUIRED_DISCHARGE_COPY;
    // Initiate ladder (discharge-final C1) — SAME sentences `validateConfirmedDod`
    // shows inline (ONE source per guard). The client mirror pre-empts them; these
    // fire on a stale-state race or a non-UI caller.
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_BEFORE_ADMISSION:
      return DOD_BEFORE_ADMISSION_ERROR(extras.admitted_at);
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_IN_FUTURE:
      return DOD_IN_FUTURE_ERROR;
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_ADMIN_ONLY:
      return extras.earliest ? DOD_ADMIN_ONLY_ERROR_WITH_DATE(formatYmd(extras.earliest)) : DOD_ADMIN_ONLY_ERROR;
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_TOO_OLD:
      return DOD_TOO_OLD_ERROR;
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_FY_MISMATCH:
      return DOD_FY_MISMATCH_ERROR(extras.fy);
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_REASON_REQUIRED:
      return DOD_REASON_REQUIRED_ERROR;
    // Legacy pre-v3 plan guard codes: repointed at the same sentences, never a second wording.
    case DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_TOO_OLD:
      return DOD_TOO_OLD_ERROR;
    case DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_IN_FUTURE:
      return DOD_IN_FUTURE_ERROR;
    case DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_BACKDATED_ADMIN_ONLY:
      return DOD_ADMIN_ONLY_ERROR;
    case DISCHARGE_ERROR_CODE.PLANNED_DISCHARGE_FY_MISMATCH:
      return DOD_FY_MISMATCH_ERROR(istFinancialYear(new Date().toISOString()));
    // PATCH / summary PUT after Initiate: the date is final. A closed stay is
    // sent to the correction rail. (FY_LOCKED and STAMP_CHOICE_REQUIRED are
    // retired server-side — no longer mapped.)
    case DISCHARGE_ERROR_CODE.DISCHARGE_DATE_READ_ONLY:
      return isTerminal(extras.status)
        ? DOD_NOT_EDITABLE_ERROR
        : DOD_READ_ONLY_ERROR;
    default:
      return null;
  }
}

/**
 * §7 confirmation-modal content (in-app ConfirmationModal — never a native
 * dialog). `gate` is the object returned by `buildDischargeGate`.
 *
 * DOD rule v3 (C5) — every confirmation carries exactly ONE discharge-date
 * statement (D1 — "exactly ONE date statement visible at all times"):
 *   - a confirmed date on the SAME IST calendar day as now (or none, or a
 *     dues-family type) → one line, appended last.
 *   - NORMAL/REFERRED with a confirmed date on a DIFFERENT IST day → the
 *     line is REPLACED by `stampChoice`: the caller (DischargeDialog) renders
 *     the Confirmed-vs-Now radios in its place — that choice IS the one date
 *     statement. "Now" is preselected.
 * Single source: `formatDischargeDateTimeIST` (FE-2) / `resolveDischargeDateTimeIso`
 * (D1) — never a second date formatter/resolver here.
 */
export function buildCompleteConfirmation({
  dischargeType, patientName, gate, unsettledLabPaise, confirmedDischargeAt = null,
  now = new Date(), deathAt = null, fyAnchor = null,
}) {
  const name = patientName || 'this patient';
  // R3/R5 (round 2, COPY-R2.md (b)) — the lab-absorb write-off selector.
  // `gate.labWriteOffAuthorised` already encodes duesEligible && balance<=0
  // && (server told us so) && canWriteOffDues — the exact state/permission
  // condition COPY-R2.md names. The amount is separate: it is only known
  // when the triggering refusal happened to carry `unsettled_lab_paise`
  // (AuthorisedByRequired never does), so AMOUNT vs NO-AMOUNT is decided
  // independently, at modal-build time.
  const labWriteOff = gate.labWriteOffAuthorised;
  const labAmount = Number(unsettledLabPaise);
  const labTotal = Number.isFinite(labAmount) && labAmount > 0
    ? describeBalance(Math.abs(labAmount)).text
    : null;

  let title;
  let body;
  if (dischargeType === DISCHARGE_TYPE.DECEASED) {
    // bk-584 REVERSED (owner ruling 2026-08-29, bk-781) — a positive
    // residual is now a write-off, byte-identical to LAMA/DAMA (it used to
    // stay owed, dues_status=OPEN, a DISTINCT body from the LAMA/DAMA
    // "will be recorded as a write-off" copy — that distinction is gone).
    title = `Complete death discharge for ${name}?`;
    body = gate.writeOffEligible
      ? `The cause of death will be recorded on the discharge summary. ${gate.balance.text} will be recorded as a write-off (bad debt) against this admission, authorised by an admin. This cannot be undone.`
      : (labWriteOff
        ? (labTotal
          ? `The cause of death will be recorded on the discharge summary. Unpaid lab charges of ${labTotal} will be recorded as a write-off (bad debt) against this admission, authorised by an admin. This cannot be undone.`
          : 'The cause of death will be recorded on the discharge summary. Unpaid lab charges will be recorded as a write-off (bad debt) against this admission, authorised by an admin. This cannot be undone.')
        : 'The cause of death will be recorded on the discharge summary.');
  } else if (dischargeType === DISCHARGE_TYPE.LAMA || dischargeType === DISCHARGE_TYPE.DAMA) {
    const word = dischargeType === DISCHARGE_TYPE.LAMA ? 'LAMA' : 'DAMA';
    title = `Complete ${word} discharge for ${name}?`;
    // R3-4 — a residual on a dues-eligible type is now a WRITE-OFF (bad
    // debt), never "recorded as dues"; only reachable once authorised.
    body = labWriteOff
      ? (labTotal
        ? `Unpaid lab charges of ${labTotal} will be recorded as a write-off (bad debt) against this admission, authorised by an admin. The bed will be freed and the final bill issued with the write-off noted. This cannot be undone.`
        : 'Unpaid lab charges will be recorded as a write-off (bad debt) against this admission, authorised by an admin. The bed will be freed and the final bill issued with the write-off noted. This cannot be undone.')
      : (gate.writeOffEligible
        ? `${gate.balance.text} will be recorded as a write-off (bad debt) against this admission, authorised by an admin. The bed will be freed and the final bill issued with the write-off noted. This cannot be undone.`
        : 'The bed will be freed and the final bill issued. This cannot be undone.');
  } else {
    title = `Complete discharge for ${name}?`;
    // A withheld balance (admission-access) cannot be claimed "settled at ₹0".
    body = gate.balanceKnown
      ? 'The bill is settled at ₹0. The bed will be freed and the final bill number issued. This cannot be undone.'
      : NO_BILL_CONFIRM_BODY;
  }

  // Complete stamps the date finalised at Initiate (no choice). Single source:
  // `resolveDischargeDateTimeIso`, the SAME function the dialog's read-only field calls.
  let confirmLine;
  if (dischargeType === DISCHARGE_TYPE.DECEASED) {
    if (deathAt && deathDodFyFallback({ deathAt, fyAnchor, now })) confirmLine = DOD_DEATH_FY_FALLBACK;
    else if (deathAt) confirmLine = dodDeathLine(deathAt);
    else confirmLine = dodRecordedLine(now.toISOString());
  } else {
    confirmLine = dodRecordedLine(resolveDischargeDateTimeIso({
      dischargeType, confirmedDischargeAt, now, fyAnchor,
    }));
  }

  return {
    title,
    body: `${body}\n\n${confirmLine}`,
    confirmLabel: gate.completeLabel,
    cancelLabel: 'Go back',
  };
}

/** §7 success-modal body — bill number + freed bed (identical across all types). */
export function buildSuccessMessage({ billNumberPrinted, bedLabel }) {
  return `Bill ${billNumberPrinted || '—'} is ready.${bedLabel ? ` Bed ${bedLabel} is now free.` : ''}`;
}
